import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import api from "../helpers/api";
import {
  countryOptions,
  formatCreatedDate,
  formatRoleName,
  userRoles,
  allRoles,
} from "../constants/utils";
import { getPresignedUrl } from "../helpers/fileUpload";
import * as XLSX from "xlsx";
import FileModal from "../components/Common/importExportModal";
import "react-phone-number-input/style.css";
import Select from "react-select";
import UserModal from "../components/UserModal";
import TicketDetailsModal from "../components/TicketDetailsModal";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from 'react-i18next';

const Users = ({ onInvite }) => {
  const { t } = useTranslation();
  document.title = `${t('shared.users', 'Users')} | ArcMedix`;
  // User modal state
  const [userModal, setUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
   const [filterModal, setFilterModal] = useState(false);
    const [enumData, setEnumData] = useState([]);
   const [filters, setFilters] = useState({
  role: "",
  verified: "",
});
  const [ticketModal, setTicketModal] = useState({
    isOpen: false,
    ticketData: null,
    loading: false
  });
  const [reportedByModal, setReportedByModal] = useState({
    isOpen: false,
    userData: null,
    loading: false
  });
  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { isAdmin, hasPermission } = usePermission();
  // Organization modal state
  const [organizationModal, setOrganizationModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    imageUrl: "",
  });
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableRef = useRef(null);
  const [fileModal, setFileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
    title: ""
  });
  const [moreDropdown, setMoreDropdown] = useState({});
  const [removeOrgModal, setRemoveOrgModal] = useState({
    isOpen: false,
    loading: false,
    user: null,
    isSelf: false,
    error: ""
  });

  const toggleDropdown = (userId) => {
    setDropdownOpen(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const toggleMoreDropdown = (userId) => {
    setMoreDropdown(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const openRemoveOrgModal = (user, isSelf) => {
    setRemoveOrgModal({
      isOpen: true,
      loading: false,
      user,
      isSelf,
      error: ""
    });
  };

  const handleRemoveFromOrganization = async () => {
    setRemoveOrgModal(prev => ({ ...prev, loading: true, error: "" }));

    try {
      if (removeOrgModal.isSelf) {
        await api.delete("user/leave-organization");
        // Clear auth and redirect to login
        localStorage.removeItem("authUser");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        await api.delete(`user/delete-user?requestId=${removeOrgModal.user.id}`);
        setRemoveOrgModal({ isOpen: false, loading: false, user: null, isSelf: false, error: "" });
        reloadUsers();
      }
    } catch (error) {
      console.error("Error removing user from organization:", error);
      setRemoveOrgModal(prev => ({
        ...prev,
        loading: false,
        error: error?.response?.data?.message || t('business.users.failed_remove_user', 'Failed to remove user. Please try again.')
      }));
    }
  };

  const handleAttendanceStatusChange = async (userId, newStatus) => {
    try {
        await api.patch(`user/mark-offline?userId=${userId}`);
      reloadUsers(); // Refresh the user list
    } catch (error) {
      console.error('Failed to update attendance status:', error);
      
      // Handle 400 error for unresolved work orders
      if (error.response?.status === 400 && error.response?.data?.message) {
        setErrorModal({
          isOpen: true,
          title: t('business.users.cannot_update_attendance', 'Cannot Update Attendance'),
          message: error.response.data.message
        });
      } else {
        // Handle other errors
        setErrorModal({
          isOpen: true,
          title: t('shared.error', 'Error'),
          message: t('business.users.attendance_update_failed', 'Failed to update attendance status. Please try again.')
        });
      }
    }
  };

  const getAttendanceStatusBadge = (status) => {
    const statusConfig = {
      'ONLINE': { color: 'success', icon: 'circle', label: t('shared.attendance_online', 'Online') },
      'OFFLINE': { color: 'secondary', icon: 'circle', label: t('shared.attendance_offline', 'Offline') }
    };
    
    const config = statusConfig[status] || statusConfig['OFFLINE'];
    
    return (
      <Badge color={config.color} pill className="py-1 px-2 d-flex align-items-center gap-1">
        <FeatherIcon icon={config.icon} size={8} />
        {config.label}
      </Badge>
    );
  };

  // Toggle user modal
  function toggleUserModal(user = null) {
    setUserModal(!userModal);
    setSelectedUser(user || {});
    setErrors({});
    setApiError("");
  }

  // Toggle organization modal
  function toggleOrganizationModal(organization = null) {
    setOrganizationModal(!organizationModal);
    setSelectedOrganization(organization || {});
    setErrors({});
    setApiError("");
  }

  const handleReportedByClick = async (userId, fromDate = null, toDate = null) => {
    if (!userId) return;

    setReportedByModal(prev => ({
      ...prev,
      isOpen: true,
      userData: null,
      loading: true
    }));

    try {
      const { data: userData } = await api.get(`user/get-all?id=${userId}`);

      const params = { 
        reportedBy: userId,
        organizationId,
        count: 5
      };

      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });

      setReportedByModal(prev => ({
        ...prev,
        userData: {
          ...userData.response[0],
          reportedIssues: issuesData.response || []
        },
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching reported by details:', error);
      setReportedByModal(prev => ({ ...prev, loading: false }));
    }
  };
  
    const onStatusChange = async (data, value,id) => {
      try {
        const payload = { id: data?.id, status: value };
        // Set resolvedAt timestamp when marking as RESOLVED
        if (value === 'RESOLVED') {
          payload.resolvedAt = new Date().toISOString();
        }
        await api.put('asset-issues/update', payload);
        handleReportedByClick(id)
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    };

  const openImagePopup = (imageUrl) => {
    setImagePopup({
      isOpen: true,
      imageUrl: imageUrl,
    });
  };

    const fetchEnums = async () => {
      try {
        const { data } = await api.get("dynamic-enum/get-all");
        setEnumData(data.response || []);
      } catch (error) {
        console.error("Error fetching enums:", error);
      }
    };

  const closeImagePopup = () => {
    setImagePopup({
      isOpen: false,
      imageUrl: "",
    });
  };

  const handleUserModalClose = () => {
    setUserModal(false);
    setSelectedUser(null);
  };

  const handleOrganizationModalClose = () => {
    setOrganizationModal(false);
    setSelectedOrganization(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
    const publicURL = await getPresignedUrl(file);

        if (selectedUser) {
          setSelectedUser({ ...selectedUser, profileUrl: publicURL });
        } else if (selectedOrganization) {
          setSelectedOrganization({ ...selectedOrganization, profileUrl: publicURL });
        }
    } catch (err) {
      setApiError(t('shared.error_during_upload', '❌ Error during upload:'), err);
    }
  };



const handleOrganizationUpdate = async (e) => {
  e.preventDefault();

  const newErrors = {};
  if (!selectedOrganization?.name?.trim())
    newErrors.name = t('shared.organization_name_is_required', 'Organization name is required');
  if (!selectedOrganization?.address?.trim())
    newErrors.address = t('shared.address_is_required', 'Address is required');
  if (!selectedOrganization?.email?.trim())
    newErrors.email = t('shared.email_is_required', 'Email is required');
  if (!selectedOrganization?.contactNumber?.trim())
    newErrors.contactNumber = t('shared.contact_number_is_required', 'Contact number is required');

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  try {
    setApiError("");

    // Remove `id` before sending
    const { id, ...organizationData } = selectedOrganization;
    const payload = [organizationData];

    const endpoint = "organization/create";
    const {data} = await api.post(endpoint, payload);
    
    const organizationId = data?.response[0]?.id;
    const userId = selectedUser?.id;

    if (organizationId && userId) {
      const assignEndpoint = `organization/assign?organizationId=${organizationId}&userId=${userId}`;
      await api.get(assignEndpoint);
    }

    reloadUsers();
    handleOrganizationModalClose();
  } catch (error) {
    setApiError(error?.response?.data?.error || t('shared.something_went_wrong', 'Something went wrong!'));
  }
};

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
    };
    reader.readAsArrayBuffer(file);
  };

    const handleTicketClick = async (ticketNumber) => {
      setTicketModal(prev => ({
        ...prev,
        isOpen: true,
        ticketData: null,
        loading: true
      }));
  
      try {
        const { data } = await api.get(`asset-issues/organization/filtered`, {
          params: { 
            ticket: ticketNumber
          }
        });
        
        setTicketModal(prev => ({
          ...prev,
          ticketData: data.response?.[0] || null,
          loading: false
        }));
      } catch (error) {
        console.error('Error fetching ticket details:', error);
        setTicketModal(prev => ({ ...prev, loading: false }));
      }
    };

  const handleExport = () => {
    const dataToExport =
      users?.map((user) => ({
        "Profile URL": user?.profileUrl || "",
        Name: user?.name || "",
        Username: user?.username || "",
        Role: formatRoleName(user?.role, t) || "",
        Email: user?.email || "",
        "Contact Number": user?.contactNumber || "",
      })) || [];

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(
      wb,
      `users_export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const validateUserForm = () => {
    const newErrors = {};

    if (!selectedUser?.name?.trim()) {
      newErrors.name = t('shared.name_is_required', 'Name is required');
    }

    if (!selectedUser?.email?.trim()) {
      newErrors.email = t('shared.email_is_required', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedUser.email)) {
      newErrors.email = t('shared.invalid_email', 'Please enter a valid email address');
    }

    if (!selectedUser?.role) {
      newErrors.role = t('business.users.role_required', 'Role is required');
    }

    if (selectedUser?.contactNumber) {
      const phoneNumber = selectedUser.contactNumber.replace(/\D/g, "");
      if (phoneNumber.length < 7) {
        newErrors.contactNumber = t('business.users.phone_min_digits', 'Phone number must be at least 7 digits');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUserUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");

    if (!validateUserForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Separate phoneCode and contactNumber
      const phoneCode = selectedUser?.phoneCode || "";
      const fullContactNumber = selectedUser?.contactNumber || "";
      const contactNumber = fullContactNumber.replace(phoneCode, "");

      const updatedEntry = {
        ...selectedUser,
        organizationId,
        phoneCode,
        contactNumber,
      };

      await api.put("user/update-user", updatedEntry);

      // Hourly rate lives on a separate, permission-gated endpoint.
      // When the editor has cost:configure-rates, send the rate alongside the update so the
      // form's single Update button persists both in one click.
      if (hasPermission && hasPermission('cost:configure-rates') && selectedUser?.id) {
        const rawRate = selectedUser?.hourlyRate;
        const parsedRate = rawRate === '' || rawRate === null || rawRate === undefined
          ? null
          : Number(rawRate);
        if (parsedRate === null || (!Number.isNaN(parsedRate) && parsedRate >= 0)) {
          try {
            await api.put(
              `cost-config/user-hourly-rate?id=${selectedUser.id}`,
              { hourlyRate: parsedRate }
            );
          } catch (rateErr) {
            setApiError(
              rateErr?.response?.data?.message
                || t('cost_tracking.user.rate_save_error', 'User saved, but hourly rate update failed.')
            );
            setIsSubmitting(false);
            return;
          }
        }
      }

      handleUserModalClose();
      reloadUsers();
    } catch (error) {
      console.error("Error during submission:", error);
      setApiError(error?.response?.data?.message || t('shared.error_submission', 'An error occurred during submission'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const { data } = await api.get("organization/all");
      setOrganizations(data.response);
    } catch (error) {
      // Full users may not have permission to fetch all organizations
      console.log('Could not fetch organizations:', error.message);
      setOrganizations([]);
    }
  };


  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setUsers([]);
    setCount(10);
    setHasMore(true);
  };

const reloadUsers = useCallback(async () => {
  setLoading(true);
  setCount(10); // Reset count when filters change
  try {
    // Build query string based on filters
    const queryParams = new URLSearchParams();

    if (filters.role) queryParams.append('role', filters.role);
    if (filters.verified) queryParams.append('verified', filters.verified);
    if (searchTerm) queryParams.append('search', searchTerm);

    const { data } = await api.get(
      `user/get-all?${queryParams.toString()}&organizationId=${organizationId}`
    );

    const usersList = data.response || [];
    setUsers(usersList);
    setHasMore(usersList.length >= 10);
  } catch (error) {
    console.error("Error reloading users:", error);
  } finally {
    setLoading(false);
  }
}, [filters, searchTerm, organizationId]);

  const loadMoreUsers = async () => {
  if (loading || !hasMore) return;

  setLoading(true);
  try {
    const newCount = count + 10;

    // Build query string with all current filters
    const queryParams = new URLSearchParams();
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.verified) queryParams.append('verified', filters.verified);
    if (searchTerm) queryParams.append('search', searchTerm);
    queryParams.append('count', newCount);

    const { data } = await api.get(
      `user/get-all?${queryParams.toString()}&organizationId=${organizationId}`
    );

    const usersList = data.response || [];
    setUsers(usersList);
    setCount(newCount);
    setHasMore(usersList.length === newCount);
  } catch (error) {
    console.error("Error loading more users:", error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    reloadUsers();
  }, [reloadUsers]);

  useEffect(() => {
    fetchOrganizations();
    fetchEnums()
  }, []);

  return (
    <>
      <div className="mb-4 d-flex align-items-center gap-3">
        {onInvite && (
          <PermissionGuard permission="invitation:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
              onClick={onInvite}
            >
              <FeatherIcon icon="user-plus" size={18} />
              {t('shared.invite_user', 'Invite User')}
            </button>
          </PermissionGuard>
        )}
        <div style={{ minWidth: "300px", flexGrow: 1, maxWidth: "500px" }}>
          <Input
            type="text"
            placeholder={t('shared.search_users', 'Search Users...')}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t('shared.users', 'Users')}</h3>
          <div className="d-flex align-items-center gap-2">
                <button
              className="btn btn-soft-primary p-2"
              onClick={() => setFilterModal(true)}
              title={t('shared.filter_users', 'Filter users')}
            >
              <FeatherIcon icon="filter" size={18} />
            </button>
            <button
              className="btn btn-soft-info p-2"
              onClick={() => setFileModal(true)}
              title={t('business.users.import_export_users', 'Import/Export users')}
            >
              <FeatherIcon icon="file-text" size={18} />
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            ref={tableRef}
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: "0.1rem",
                borderCollapse: "separate",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>{t('shared.name', 'Name')}</th>
                  <th>{t('shared.role', 'Role')}</th>
                  <th>{t('shared.email', 'Email')}</th>
                  <th>{t('shared.contact_number', 'Contact Number')}</th>
                  <th>{t('shared.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {users?.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {user?.profileUrl && (
                            <img
                              alt="profile-pic"
                              src={user.profileUrl}
                              height={27}
                              width={27}
                              className="rounded-circle"
                              style={{ cursor: "pointer", objectFit: "cover" }}
                              onClick={() => openImagePopup(user.profileUrl)}
                            />
                          )}
                          <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => handleReportedByClick(user?.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td>{formatRoleName(user?.role, t)}</td>
                      <td>{user.email}</td>
                      <td>{user.phoneCode ? `+${user.phoneCode} ` : ''}{user.contactNumber}</td>
                      <td>
                        <Dropdown
                          isOpen={moreDropdown[user.id] || false}
                          toggle={() => toggleMoreDropdown(user.id)}
                          direction="down"
                        >
                          <DropdownToggle
                            tag="div"
                            className="d-inline-block"
                            style={{ cursor: 'pointer' }}
                          >
                            <FeatherIcon icon="more-vertical" size={18} className="text-muted" />
                          </DropdownToggle>
                          <DropdownMenu container="body">
                            <PermissionGuard permission="user:update">
                              <DropdownItem
                                className="d-flex align-items-center gap-2"
                                onClick={() => toggleUserModal(user)}
                              >
                                <FeatherIcon icon="edit" size={14} />
                                {t('shared.edit', 'Edit')}
                              </DropdownItem>
                            </PermissionGuard>
                            {user.id === currentUser?.id ? (
                              <DropdownItem
                                className="d-flex align-items-center gap-2 text-danger"
                                onClick={() => openRemoveOrgModal(user, true)}
                              >
                                <FeatherIcon icon="log-out" size={14} />
                                {t('business.users.leave_organization', 'Leave Organization')}
                              </DropdownItem>
                            ) : (
                              <PermissionGuard permission="user:delete">
                                <DropdownItem
                                  className="d-flex align-items-center gap-2 text-danger"
                                  onClick={() => openRemoveOrgModal(user, false)}
                                >
                                  <FeatherIcon icon="user-minus" size={14} />
                                    {t('business.users.remove_from_organization', 'Remove from Organization')}
                                </DropdownItem>
                              </PermissionGuard>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {loading && (
              <div className="text-center py-3">
                <Spinner color="primary" />
              </div>
            )}
            {!loading && users.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">{t('business.users.no_users_found', 'No users found')}</p>
              </div>
            )}
          </div>
         {hasMore && users.length > 0 && !loading && (
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  onClick={loadMoreUsers}
                  disabled={loading}
                >
                  {loading ? t('shared.loading', 'Loading...') : t('shared.load', 'Load More')}
                </Button>
              </div>
            )}
          {!hasMore && users.length > 0 && !loading && (
            <div className="text-center py-2 mt-3 text-muted">
              {t('business.users.no_more_users', 'No more users to load')}
            </div>
          )}
        </CardBody>
      </Card>

      {/* User Modal */}
    <Modal
        size="lg"
        isOpen={userModal}
        toggle={() => {
          toggleUserModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
                {t('business.users.edit_user', 'Edit User')}
            </h5>
            <button
              onClick={handleUserModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleUserUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.name', 'Name')}
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedUser?.name || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        name: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_name', 'Enter name')}
                    id="horizontal-name-input"
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>

                  <Row className="mb-4">
                  <label
                    htmlFor="horizontal-contact-input"
                    className="col-sm-3 col-form-label"
                  >
                    {t('shared.contact_number', 'Contact Number')}
                  </label>
                  <Col sm={9}>
                    <div className="d-flex gap-2">
                      {/* Country Code Select */}
                      <Select
                        options={countryOptions}
                        value={countryOptions.find((c) =>
                          selectedUser?.phoneCode === c.dialCode.replace("+", "")
                        )}
                        onChange={(selected) => {
                          const dialCode = selected?.dialCode.replace("+", "") || "";
                          const phoneNumber = selectedUser?.contactNumber?.replace(/\D/g, "") || "";

                          setSelectedUser({
                            ...selectedUser,
                            phoneCode: dialCode,
                            // Keep the existing phone number digits
                            contactNumber: dialCode + phoneNumber
                          });
                        }}
                        filterOption={(option, inputValue) => {
                          if (!inputValue) return true;
                          const searchText = inputValue.toLowerCase();
                          const labelText = option.data.labelText?.toLowerCase() || '';
                          const name = option.data.name?.toLowerCase() || '';
                          const dialCode = option.data.dialCode?.toLowerCase() || '';
                          return labelText.includes(searchText) ||
                                name.includes(searchText) ||
                                dialCode.includes(searchText);
                        }}
                        className="flex-grow-0"
                        styles={{
                          control: (base) => ({
                            ...base,
                            width: "150px",
                          }),
                        }}
                        isClearable
                        placeholder={t('shared.code', 'Code')}
                      />

                      {/* Phone Number Input */}
                      <input
                        type="tel"
                        className={`form-control flex-grow-1 ${
                          errors.contactNumber ? "is-invalid" : ""
                        }`}
                        value={selectedUser?.contactNumber?.replace(/\D/g, "").replace(selectedUser?.phoneCode || "", "") || ""}
                        onChange={(e) => {
                          const phone = e.target.value.replace(/\D/g, "");
                          const dialCode = selectedUser?.phoneCode || "";
                          setSelectedUser({
                            ...selectedUser,
                            contactNumber: dialCode + phone
                          });
                        }}
                        placeholder={t('shared.phone_number', 'Phone number')}
                      />
                    </div>
                    {errors.contactNumber && (
                      <small className="text-danger">
                        {errors.contactNumber}
                      </small>
                    )}
                  </Col>
                </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-email-input"
                  className="col-sm-3 col-form-label"
                >
                    {t('shared.email', 'Email')}

                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedUser?.email || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        email: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email address"
                    id="horizontal-email-input"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-role-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.role', 'Role')}
                </label>
                <Col sm={9}>
                  <select
                    className={`form-select ${
                      errors.role ? "is-invalid" : ""
                    }`}
                    value={selectedUser?.role || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        role: e.target.value,
                      })
                    }
                    id="horizontal-role-input"
                  >
                    <option value="">{t('shared.select_role', 'Select Role')}</option>
                    {allRoles.map((role) => (
                      <option key={role} value={role}>
                        {formatRoleName(role, t)}
                      </option>
                    ))}
                  </select>
                  {errors.role && (
                    <small className="text-danger">{errors.role}</small>
                  )}
                </Col>
              </Row>
              <PermissionGuard permission="cost:configure-rates">
                <Row className="mb-4">
                  <label
                    htmlFor="horizontal-hourly-rate-input"
                    className="col-sm-3 col-form-label"
                  >
                    {t('cost_tracking.user.hourly_rate', 'Hourly rate')}
                  </label>
                  <Col sm={9}>
                    <div className="input-group">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        placeholder={t('cost_tracking.user.hourly_rate_placeholder', 'e.g. 40.00')}
                        value={selectedUser?.hourlyRate ?? ''}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            hourlyRate: e.target.value,
                          })
                        }
                        id="horizontal-hourly-rate-input"
                      />
                      <span className="input-group-text">{currentUser?.activeOrganizationCurrency || 'USD'}</span>
                    </div>
                    <small className="text-muted">
                      {t('cost_tracking.user.hourly_rate_hint', 'Saved with Update. Leave blank to clear.')}
                    </small>
                  </Col>
                </Row>
              </PermissionGuard>
              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('shared.processing', 'Processing...') : t('shared.update', 'Update')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Organization Modal */}
      <Modal
        size="lg"
        isOpen={organizationModal}
        toggle={() => {
          toggleOrganizationModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedOrganization?.id && selectedOrganization.id !== -1 ? t('shared.edit_organization', 'Edit Organization') : t('shared.add_organization', 'Add Organization')}
            </h5>
            <button
              onClick={handleOrganizationModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleOrganizationUpdate}>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t('shared.profile_picture', 'Profile Picture')}
                </label>
                <Col sm={9}>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                  />
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t('shared.name', 'Name')}
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedOrganization?.name || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        name: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder={t('shared.enter_name_of_the_organization', 'Enter Name of the Organization')}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t('shared.address', 'Address')}
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedOrganization?.address || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        address: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder={t('shared.enter_address', 'Enter the Address')}
                  />
                   {errors.address && (
                    <small className="text-danger">{errors.address}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t('shared.email', 'Email')}
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedOrganization?.email || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        email: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder={t('shared.enter_email_address', 'Enter the Email Address')}
                  />
                     {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t('shared.contact_number', 'Contact Number')}
                </label>
                <Col sm={9}>
                  <input
                    className="form-control"
                    value={selectedOrganization?.contactNumber || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        contactNumber: e.target.value,
                      })
                    }
                    type="tel"
                    placeholder={t('shared.enter_contact_number', 'Enter the Contact Number')}
                  />
                         {errors.contactNumber && (
                    <small className="text-danger">
                      {errors.contactNumber}
                    </small>
                  )}
                </Col>
              </Row>
                <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                >
                  {t('shared.add', 'Add')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>



      <Modal
        isOpen={imagePopup.isOpen}
        toggle={closeImagePopup}
        centered
        size="md"
      >
        <div className="modal-header">
          <button
            type="button"
            className="btn-close"
            onClick={closeImagePopup}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body text-center">
          <img
            src={imagePopup.imageUrl}
            alt="Profile"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
            }}
          />
        </div>
      </Modal>
      <UserModal
        isOpen={reportedByModal.isOpen}
        toggle={() => setReportedByModal(prev => ({ ...prev, isOpen: false }))}
        userData={reportedByModal.userData}
        loading={reportedByModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
        fetchUserWorkOrders={handleReportedByClick}
      />
       {/* User Filter Modal */}
      <Modal
        isOpen={filterModal}
        toggle={() => setFilterModal(false)}
        size="md"
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
        centered
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t('shared.filter_users', 'Filter Users')}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setFilterModal(false)}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="filterRole" className="form-label fw-medium">
                    {t('shared.role', 'Role')}
                  </label>
                  <select
                    id="filterRole"
                    className="form-select"
                    value={filters.role}
                    onChange={(e) =>
                      setFilters({ ...filters, role: e.target.value })
                    }
                  >
                    <option value="">{t('business.users.all_roles', 'All Roles')}</option>
                    {Object.values(userRoles).flat().map((role) => (
                      <option key={role} value={role}>
                        {formatRoleName(role, t)}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="filterVerified" className="form-label fw-medium">
                    {t('shared.verification_status', 'Verification Status')}
                  </label>
                  <select
                    id="filterVerified"
                    className="form-select"
                    value={filters.verified}
                    onChange={(e) =>
                      setFilters({ ...filters, verified: e.target.value })
                    }
                  >
                    <option value="">{t('business.users.all_users', 'All Users')}</option>
                    <option value="true">{t('business.users.verified_only', 'Verified Only')}</option>
                    <option value="false">{t('business.users.not_verified_only', 'Not Verified Only')}</option>
                  </select>
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setFilters({
                  role: "",
                  verified: "",
                });
                setFilterModal(false);
              }}
            >
              {t('shared.clear_filters', 'Clear Filters')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setFilterModal(false);
                reloadUsers();
              }}
            >
              {t('shared.apply_filters', 'Apply Filters')}
            </button>
          </div>
        </motion.div>
      </Modal>

      <FileModal
        title={t('shared.users', 'Users')}
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={organizations}
        importNeed={false}
        error={apiError}
        loading={loading}
        onImport={handleImport}
        onExport={handleExport}
        importLabel={t('business.users.import_users_excel', 'Import Users from Excel')}
        exportLabel={t('business.users.export_users_excel', 'Export Users to Excel')}
      />
      <TicketDetailsModal
      isOpen={ticketModal.isOpen}
      enumData={enumData}
      toggle={() => setTicketModal(prev => ({ ...prev, isOpen: false }))}
      ticketNumber={ticketModal.ticketData?.ticketNumber}
      loading={ticketModal.loading}
      formatCreatedDate={formatCreatedDate}
      onStatusChange={onStatusChange}
      />

      {/* Error Modal for Unresolved Work Orders */}
      <Modal isOpen={errorModal.isOpen} toggle={() => setErrorModal({ isOpen: false, message: "", title: "" })} centered>
        <div className="modal-header">
          <h5 className="modal-title text-danger">
            <FeatherIcon icon="alert-circle" className="me-2" />
            {errorModal.title}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorModal({ isOpen: false, message: "", title: "" })}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning d-flex align-items-center">
            <FeatherIcon icon="alert-triangle" className="me-2" />
            <div>
              {errorModal.message}
            </div>
          </div>
          <p className="mb-0 text-muted">
            {t('business.users.reassign_work_orders_warning', 'Please assign the unresolved work orders to another technician before updating the attendance status.')}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setErrorModal({ isOpen: false, message: "", title: "" })}
          >
            {t('shared.understood', 'Understood')}
          </button>
        </div>
      </Modal>

      {/* Leave / Remove from Organization Modal */}
      <Modal
        isOpen={removeOrgModal.isOpen}
        toggle={() => !removeOrgModal.loading && setRemoveOrgModal(prev => ({ ...prev, isOpen: false, error: "" }))}
        centered
        size="md"
      >
        <div className="modal-header">
          <h5 className="modal-title d-flex align-items-center gap-2">
            <FeatherIcon icon={removeOrgModal.isSelf ? "log-out" : "user-minus"} size={18} />
            {removeOrgModal.isSelf ? "Leave Organization" : "Remove from Organization"}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => !removeOrgModal.loading && setRemoveOrgModal(prev => ({ ...prev, isOpen: false, error: "" }))}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          {removeOrgModal.isSelf ? (
            <p>{t('business.users.leave_organization_alert', 'Are you sure you want to leave this organization? You will be logged out and will no longer have access.')}</p>
          ) : (
            <p>{t('business.users.remove_from_organization_alert', 'Are you sure you want to remove')} <strong>{removeOrgModal.user?.firstName} {removeOrgModal.user?.lastName}</strong> {t('business.users.remove_from_organization_alert_access','from the organization? They will no longer have access.')}</p>
          )}
          {removeOrgModal.error && (
            <div className="alert alert-danger">{removeOrgModal.error}</div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setRemoveOrgModal(prev => ({ ...prev, isOpen: false, error: "" }))}
            disabled={removeOrgModal.loading}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleRemoveFromOrganization}
            disabled={removeOrgModal.loading}
          >
            {removeOrgModal.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {removeOrgModal.isSelf ? "Leaving..." : "Removing..."}
              </>
            ) : (
              removeOrgModal.isSelf ? t('business.users.leave_orgabization', 'Leave Organization') : t('business.users.remove_from_organization', 'Remove from Organization')
            )}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Users;